import { ref } from 'vue';
import type { Ref } from 'vue';

// Cross-linked block selection for the combined scheduling view: one block is selected
// page-wide — a PO by its poNumber, a manual block by its group key (distributionGroupId
// or assignment id). The Scheduled row rings its block and the type rows ring its
// segments while dimming every other assignment segment.
// Each call owns its selection: the view creates one and passes it to whatever needs it,
// rather than every consumer calling this and getting its own disconnected ref.
// Which card the selection came from, where it came from a card at all. The week is part of
// the identity because the capacity bars key their segments by poNumber — a segment cannot say
// which week's assignment it belongs to, but the bar it sits in can.
export interface SelectedBlock {
  blockKey: string;
  weekKey: string;
}

export interface SelectedPo {
  selectedPoNumber: Ref<string | null>;
  // The same PO can sit in several weeks: its number identifies the family, this identifies
  // the member. The board rings this one and leaves the rest merely tinted.
  selectedBlock: Ref<SelectedBlock | null>;
  select: (poNumber: string | null, block?: SelectedBlock | null) => void;
  toggle: (poNumber: string | null, block?: SelectedBlock | null) => void;
  clear: () => void;
}

export function useSelectedPo(): SelectedPo {
  const selectedPoNumber = ref<string | null>(null);
  const selectedBlock = ref<SelectedBlock | null>(null);

  // block is optional because selecting from a capacity segment names a PO without pointing at
  // a card; leaving it out means no card is the anchor, rather than the previously clicked one
  // staying ringed under a new selection.
  function select(poNumber: string | null, block: SelectedBlock | null = null): void {
    selectedPoNumber.value = poNumber;
    selectedBlock.value = poNumber === null ? null : block;
  }

  function toggle(poNumber: string | null, block: SelectedBlock | null = null): void {
    if (poNumber !== null && selectedPoNumber.value === poNumber) {
      clear();
      return;
    }
    select(poNumber, block);
  }

  function clear(): void {
    selectedPoNumber.value = null;
    selectedBlock.value = null;
  }

  return { selectedPoNumber, selectedBlock, select, toggle, clear };
}
